/**
 * KPI settings — single source of truth for the per-client KPI configuration
 * stored in `agencyClients.kpiSettings` (jsonb) and for the metric catalog that
 * drives the daily Slack KPI digest.
 *
 * Backend (Slack digest builder + PATCH endpoint) and frontend (Slack
 * notifications panel) both import from here so the businessType → metric-set
 * mapping and the KPI shape can never drift apart.
 */

import { z } from "zod";

/** Metric ids available in the digest catalog. */
export const KPI_METRIC_IDS = [
  "cost_per_lead",
  "cost_per_call",
  "cost_per_purchase",
  "roas",
  "revenue",
  "spend",
] as const;

export type KpiMetricId = (typeof KPI_METRIC_IDS)[number];

/** Business models a client can have (mirrors `agencyClients.businessType`). */
export const KPI_BUSINESS_TYPES = [
  "lead_gen",
  "vsl_funnel",
  "ecommerce",
  "saas",
  "autowebinar_book_a_call",
  "autowebinar_mid_ticket",
] as const;

export type KpiBusinessType = (typeof KPI_BUSINESS_TYPES)[number];

/** Names of the numeric target fields inside {@link KpiSettings}. */
export type KpiTargetField =
  "targetCpl" | "targetCostPerCall" | "targetCostPerPurchase" | "targetRoas";

export type KpiMetricDefinition = {
  id: KpiMetricId;
  /** Polish label used by the Slack digest (frontend uses i18n keys per id). */
  label: string;
  /** kpiSettings field holding the target, or null for informational metrics. */
  targetField: KpiTargetField | null;
  /** true → lower actual is better (costs); false → higher is better (ROAS). */
  lowerIsBetter: boolean;
  /** Informational metrics (spend/revenue) render without a color/target. */
  informational: boolean;
};

/**
 * Metric catalog. `spend` is always rendered at the top of each block;
 * `revenue` is informational (no target). All others need a target to be
 * colored/shown.
 */
export const KPI_METRIC_CATALOG: Record<KpiMetricId, KpiMetricDefinition> = {
  spend: {
    id: "spend",
    label: "Wydatki",
    targetField: null,
    lowerIsBetter: false,
    informational: true,
  },
  cost_per_lead: {
    id: "cost_per_lead",
    label: "Koszt/Lead",
    targetField: "targetCpl",
    lowerIsBetter: true,
    informational: false,
  },
  cost_per_call: {
    id: "cost_per_call",
    label: "Koszt/Rozmowa",
    targetField: "targetCostPerCall",
    lowerIsBetter: true,
    informational: false,
  },
  cost_per_purchase: {
    id: "cost_per_purchase",
    label: "Koszt/Zakup",
    targetField: "targetCostPerPurchase",
    lowerIsBetter: true,
    informational: false,
  },
  roas: {
    id: "roas",
    label: "ROAS",
    targetField: "targetRoas",
    lowerIsBetter: false,
    informational: false,
  },
  revenue: {
    id: "revenue",
    label: "Przychód",
    targetField: null,
    lowerIsBetter: false,
    informational: true,
  },
};

/**
 * Default metric set per business type. Explicit metric ids from the catalog.
 * NOTE (saas): full trial/subscription tracking needs a proper conversion
 * mapping (trials/subscriptions → cost). Until that lands we approximate with
 * cost_per_lead (signup proxy) + roas — follow-up.
 */
export const DEFAULT_METRICS_BY_BUSINESS_TYPE: Record<KpiBusinessType, KpiMetricId[]> = {
  lead_gen: ["cost_per_lead", "cost_per_call"],
  vsl_funnel: ["cost_per_lead", "cost_per_call"],
  autowebinar_book_a_call: ["cost_per_lead", "cost_per_call"],
  ecommerce: ["roas", "cost_per_purchase", "revenue"],
  autowebinar_mid_ticket: ["cost_per_call", "cost_per_purchase"],
  saas: ["cost_per_lead", "roas"],
};

export function isKpiMetricId(value: unknown): value is KpiMetricId {
  return typeof value === "string" && (KPI_METRIC_IDS as readonly string[]).includes(value);
}

export function isKpiBusinessType(value: unknown): value is KpiBusinessType {
  return typeof value === "string" && (KPI_BUSINESS_TYPES as readonly string[]).includes(value);
}

/**
 * Resolve the effective metric set for a client:
 * `slackMetricsOverride` (non-null & non-empty) REPLACES the businessType
 * default; otherwise the businessType default is used. Unknown business types
 * fall back to `lead_gen`.
 */
export function resolveEffectiveKpiMetrics(
  businessType: string | null | undefined,
  override: readonly string[] | null | undefined
): KpiMetricId[] {
  if (override && override.length > 0) {
    const filtered = override.filter(isKpiMetricId);
    if (filtered.length > 0) return filtered;
  }
  const bt: KpiBusinessType = isKpiBusinessType(businessType) ? businessType : "lead_gen";
  return DEFAULT_METRICS_BY_BUSINESS_TYPE[bt];
}

/**
 * Zod schema for the full `agencyClients.kpiSettings` jsonb payload. All fields
 * optional → backward compatible with rows that only ever set targetCpl/
 * targetRoas. Unknown keys are stripped on write.
 */
export const kpiSettingsSchema = z.object({
  // Lead Gen / VSL Funnel
  targetCpl: z.number().optional(),
  minLeadsPerDay: z.number().optional(),
  targetCtr: z.number().optional(),
  // VSL Funnel specific
  targetConversionRate: z.number().optional(),
  // Ecommerce
  targetRoas: z.number().optional(),
  targetCpa: z.number().optional(),
  minDailyRevenue: z.number().optional(),
  // SaaS
  targetCpaSignup: z.number().optional(),
  targetTrialToPaid: z.number().optional(),
  targetLtvCacRatio: z.number().optional(),
  targetMonthlyChurn: z.number().optional(),
  targetPaybackMonths: z.number().optional(),
  arpu: z.number().optional(),
  trialLengthDays: z.number().optional(),
  // Autowebinar (book_a_call + mid_ticket)
  targetShowUpRate: z.number().optional(),
  targetCostPerAttendee: z.number().optional(),
  targetCostPerBooking: z.number().optional(),
  everwebinarApiKey: z.string().optional(),
  everwebinarWebinarHash: z.string().optional(),
  // Shared
  maxDailyBudget: z.number().optional(),
  /** VP-241: budzet na caly miesiac (zl). Zrodlo alertu "budzet na wyczerpaniu". */
  targetMonthlyBudget: z.number().optional(),
  notes: z.string().optional(),
  // ── Daily Slack KPI digest (VP-kpi-daily-metrics) ──
  /** Target cost per booked call/conversation (zł). */
  targetCostPerCall: z.number().optional(),
  /** Target cost per purchase (zł). */
  targetCostPerPurchase: z.number().optional(),
  /** Explicit metric ids that REPLACE the businessType default. null/absent → default. */
  slackMetricsOverride: z.array(z.enum(KPI_METRIC_IDS)).nullable().optional(),
  /** false → exclude this client from the daily digest. Defaults to enabled. */
  slackDigestEnabled: z.boolean().optional(),
});

export type KpiSettings = z.infer<typeof kpiSettingsSchema>;

/**
 * Effective KPI TARGET values for a client. These are the single source of
 * truth for KPI VALUES and live in `analyticsSettings` (per the client's user),
 * NOT in `agencyClients.kpiSettings`. Column mapping:
 *   targetCpl              ← analyticsSettings.kpiCostPerLead
 *   targetCostPerCall      ← analyticsSettings.kpiCostPerCall
 *   targetCostPerPurchase  ← analyticsSettings.kpiCostPerClient
 *   targetRoas             ← analyticsSettings.kpiTargetRoas
 */
export type ClientKpiTargets = {
  targetCpl: number | null;
  targetCostPerCall: number | null;
  targetCostPerPurchase: number | null;
  targetRoas: number | null;
};

/** All-null targets — used when a client has no analyticsSettings row yet. */
export const EMPTY_CLIENT_KPI_TARGETS: ClientKpiTargets = {
  targetCpl: null,
  targetCostPerCall: null,
  targetCostPerPurchase: null,
  targetRoas: null,
};

/**
 * Zod schema for the PATCH endpoint that edits a client's digest config from
 * the Slack panel. Only the digest-relevant subset is editable here.
 */
export const kpiSettingsPatchSchema = z
  .object({
    targetCpl: z.number().positive().nullable().optional(),
    targetCostPerCall: z.number().positive().nullable().optional(),
    targetCostPerPurchase: z.number().positive().nullable().optional(),
    targetRoas: z.number().positive().nullable().optional(),
    slackMetricsOverride: z.array(z.enum(KPI_METRIC_IDS)).nullable().optional(),
    slackDigestEnabled: z.boolean().optional(),
    // VP-241: progi alertow wynikowych. `targetCtr` istnialo juz w kpiSettings,
    // ale nie bylo edytowalne z panelu — alert "CTR spada" go potrzebuje.
    targetCtr: z.number().positive().nullable().optional(),
    targetMonthlyBudget: z.number().positive().nullable().optional(),
  })
  .strict();

export type KpiSettingsPatch = z.infer<typeof kpiSettingsPatchSchema>;
